import React from "react"
import styled from "styled-components"
import { AppFunctionComponent } from "../../../types"
import { noop } from "lodash"
import { GatsbyImageProps, IGatsbyImageData } from "gatsby-plugin-image"
import { Image } from "../../common/image.component"

const ResultsTextBase = styled.p`
  font-size: ${16 / 16}rem;
  line-height: ${32 / 16};
`

const Text = styled(ResultsTextBase)`
  span {
    opacity: 1;
    cursor: pointer;
    color: ${({ theme }) => theme.color.link};
    transition: opacity 0.5s;

    &:hover {
      opacity: 0.8;
    }
  }
`

const ItemText = styled(ResultsTextBase)`
  font-weight: 400;
  line-height: ${20 / 16}em;

  span {
    margin-left: 10px;
    font-weight: 300;
    font-size: ${12 / 16}rem;
    opacity: 0.5;
  }
`

const ResultsRowBase = styled.div`
  box-sizing: border-box;
  display: flex;
  min-height: var(--results-row-min-height);
  align-items: center;

  transition: background-color 0.5s;

  &:not(:first-of-type) {
    border-top: 1px solid ${({ theme }) => theme.color.inputBorder};
  }
`

const Row = styled(ResultsRowBase)`
  padding: 0 15px;
`

const ItemRow = styled(Row)<{ withImage?: boolean }>`
  padding: ${({ withImage }) => (withImage ? `0 5px` : `0 15px`)};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.color.inputBorder};
  }
`

const ItemRowImage = styled(Image)<GatsbyImageProps>`
  width: 40px;
  border-radius: ${({ theme }) => theme.size.borderRadius};
  margin-right: 10px;
`

const ItemsContainer = styled.div`
  max-height: calc(5 * var(--results-row-min-height));
  overflow: auto;
`

const Container = styled.div`
  --results-row-min-height: 50px;

  background-color: ${({ theme }) => theme.color.backgroundInverted};
  border-bottom: 1px solid ${({ theme }) => theme.color.inputBorder};
  border-bottom-left-radius: ${({ theme }) => theme.size.borderRadius};
  border-bottom-right-radius: ${({ theme }) => theme.size.borderRadius};
`

const NewItemLink = styled.span`
  white-space: pre;
`

export interface ResultItem {
  id: string
  title: string
  subtitle?: string
  context?: string
  image?: IGatsbyImageData
}

export interface ResultsProps {
  items: ResultItem[]
  onItemMouseDown?: (id: string) => void
  onNewItemNotifyMouseDown?: () => void
  newItemNotifyText?: string
}

// onMouseDown event is called before blur from the parent input component
// onClick event has lower priority than blur
export const Results: AppFunctionComponent<ResultsProps> = ({
  className,
  items,
  newItemNotifyText = "Didn't find anything?",
  onNewItemNotifyMouseDown,
  onItemMouseDown = noop,
}) => {
  const onItemMouseDownHandle = (id: string) => () => onItemMouseDown(id)

  return (
    <Container className={className}>
      <ItemsContainer>
        {items.map(({ image, title, subtitle, id }) => (
          <ItemRow
            key={id}
            onMouseDown={onItemMouseDownHandle(id)}
            withImage={Boolean(image)}
          >
            {image && <ItemRowImage image={image} alt="" />}
            <ItemText>
              {title}
              {subtitle && <span>({subtitle})</span>}
            </ItemText>
          </ItemRow>
        ))}
      </ItemsContainer>
      {items.length === 0 && (
        <Row>
          <Text>No results</Text>
        </Row>
      )}
      {onNewItemNotifyMouseDown && (
        <Row>
          <Text>
            {newItemNotifyText}{" "}
            <NewItemLink onMouseDown={onNewItemNotifyMouseDown}>
              Let us know
            </NewItemLink>
          </Text>
        </Row>
      )}
    </Container>
  )
}
